// Customer reels carousel: page dots that follow the scroll position on
// phones, and previous/next arrows on tablets. Scrolling itself is native
// (CSS scroll-snap), so touch, trackpad, and keyboard scrolling all work.
if (!customElements.get('nd-reel-carousel')) {
  customElements.define('nd-reel-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-reel-track]');
      if (!this.track || this.listeners) return;

      this.cards = Array.from(this.track.querySelectorAll('[data-reel-card]'));
      this.dots = Array.from(this.querySelectorAll('[data-reel-dot]'));
      this.prev = this.querySelector('[data-reel-prev]');
      this.next = this.querySelector('[data-reel-next]');
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };

      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }, { passive: true, signal: this.listeners.signal });
      window.addEventListener('resize', this.update, options);

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.scrollToCard(Number(dot.dataset.reelDot)), options);
      });
      this.prev?.addEventListener('click', () => this.scrollByCards(-1), options);
      this.next?.addEventListener('click', () => this.scrollByCards(1), options);

      this.formatCounts();
      this.update();
    }

    // Engagement counts in the page language's compact style (54K, 5.4万).
    formatCounts() {
      const locale = document.documentElement.lang || undefined;
      let format;
      try {
        format = new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 });
      } catch {
        return;
      }
      this.closest('.nd-reels, nd-reel-previews')?.querySelectorAll('[data-reel-count]').forEach((el) => {
        const value = Number(el.dataset.reelCount);
        if (Number.isFinite(value) && value > 0) el.textContent = format.format(value);
      });
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.listeners = null;
      if (this.frame) window.cancelAnimationFrame(this.frame);
    }

    step() {
      if (this.cards.length < 2) return this.track.clientWidth || 1;
      return this.cards[1].offsetLeft - this.cards[0].offsetLeft || 1;
    }

    currentIndex() {
      return Math.max(0, Math.min(this.cards.length - 1, Math.round(this.track.scrollLeft / this.step())));
    }

    visibleCount() {
      return Math.max(1, Math.floor((this.track.clientWidth + 1) / this.step()));
    }

    scrollToCard(index) {
      const card = this.cards[index];
      if (!card) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: card.offsetLeft - this.cards[0].offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    }

    scrollByCards(direction) {
      const target = this.currentIndex() + direction * this.visibleCount();
      this.scrollToCard(Math.max(0, Math.min(this.cards.length - 1, target)));
    }

    update() {
      const index = this.currentIndex();
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      // At the end of the row the last dot is current even if the first
      // visible card is earlier.
      const active = this.track.scrollLeft >= maxScroll - 2 && maxScroll > 0 ? this.cards.length - 1 : index;
      this.dots.forEach((dot, i) => {
        if (i === active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = maxScroll <= 2 || this.track.scrollLeft >= maxScroll - 2;
    }
  });
}
